"use strict";

define('rs/services/document.service',['./abstract.service', './workflow.service', 'rs/models/document', 'rs/models/template', 'rs/models/assignment.builder', 'rs/models/assignments', 'rs/models/document.decision', 'rs/models/document.letter', 'rs/models/meeting'], function (AbstractService, WorkflowService, Document, Template, AssignmentBuilder, Assignments, DocumentDecision, DocumentLetter, Meeting) {
  var DocumentService = AbstractService.extend({
    properties: ['documentId'],
    getDocument: function getDocument(force) {
      return this.cache('document', this.requestDocument, force);
    },
    getTemplate: function getTemplate(force) {
      return this.cache('template', this.requestTemplate, force);
    },
    getAnswers: function getAnswers(force) {
      return this.cache('answers', this.requestAnswers, force);
    },
    getPermissions: function getPermissions(force) {
      return this.cache('permissions', this.requestPermissions, force);
    },
    getAssignments: function getAssignments(force) {
      return this.cache('assignments', this.requestAssignments, force);
    },
    getChecklists: function getChecklists(force) {
      return this.cache('checklists', this.requestChecklists, force);
    },
    getHistoricalChecklists: function getHistoricalChecklists(force) {
      return this.cache('historicalChecklists', this.requestHistoricalChecklists, force);
    },
    getCurrentUserAssignments: function getCurrentUserAssignments() {
      var person = this.getPerson();
      return this.getAssignments().getAssignmentsByPersonId(person.getId());
    },
    getCurrentAnalyst: function getCurrentAnalyst() {
      return this.getAssignments().getOnePerson('IRB_ANALYST');
    },
    getDecisionTypes: function getDecisionTypes(force) {
      return this.cache('decisionTypes', this.requestDecisionTypes, force);
    },
    getDecisions: function getDecisions(force) {
      return this.cache('decisions', this.requestDecisions, force);
    },
    getOpenDecision: function getOpenDecision() {
      return _.find(this.getDecisions(), function (decision) {
        return !decision.getFinalizedDate();
      });
    },
    getFinalizedDecisions: function getFinalizedDecisions() {
      return _.filter(this.getDecisions(), function (decision) {
        return !!decision.getFinalizedDate();
      });
    },
    getResponsibleDecisions: function getResponsibleDecisions(lastChildrenOnly) {
      var res = [],
        decision = null,
        active = this.getOpenDecision(),
        personId = this.getPerson().getId();
      if (active) {
        decision = active.getDecisionByResponsiblePersonId(personId);
        if (decision) {
          res.push(decision);
        }
      }
      if (lastChildrenOnly) {
        res = _.filter(res, function (d) {
          return !d.children || !d.children.length;
        });
      }
      return res;
    },
    getResponsibleDecision: function getResponsibleDecision(lastChildrenOnly) {
      var responsibleDecisions = this.getResponsibleDecisions(lastChildrenOnly);
      return responsibleDecisions[0];
    },
    canPromoteDecisions: function canPromoteDecisions() {
      var openDecision = this.getOpenDecision(),
        childDecisions = openDecision ? openDecision.getChildren() : [],
        noPrimaryReviewers = !this.hasAssignment('IRB_REVIEWER_PRIMARY'),
        userIsAnalyst = this.currentUserHasAssignment('IRB_ANALYST'),
        allChildDecisionsFinalized = false;
      allChildDecisionsFinalized = _.every(childDecisions, function (decision) {
        return !!decision.finalizedDate;
      });
      return noPrimaryReviewers && userIsAnalyst && allChildDecisionsFinalized;
    },
    getLetters: function getLetters(force) {
      return this.cache('letters', this.requestLetters, force);
    },
    getSentLetters: function getSentLetters() {
      return _.filter(this.getLetters(), function (letter) {
        return !!letter.dateSent;
      });
    },
    getUnsentLetters: function getUnsentLetters() {
      return _.reject(this.getLetters(), function (letter) {
        return !!letter.dateSent;
      });
    },
    getOpenLetters: function getOpenLetters() {
      var doc = this.getDocument(),
        certifyDate = doc.getLastReadOnlyDate();
      return _.filter(this.getLetters(), function (letter) {
        return letter.getActivityDate() >= certifyDate && !letter.getDateSent();
      });
    },
    getMeetings: function getMeetings(force) {
      return this.cache('meetings', this.requestMeetings, force);
    },
    getOpenMeetings: function getOpenMeetings() {
      var now = new Date().getTime();
      return _.filter(this.getMeetings(), function (meeting) {
        return meeting.getEndDate().getTime() > now;
      });
    },
    getWorkflowService: function getWorkflowService(force) {
      return this.cache('workflowService', function () {
        return new WorkflowService({
          documentService: this
        });
      }, force);
    },
    assignMeetings: function assignMeetings(meetings) {
      var existingMeetings = this.getMeetings(),
        doc = this.getDocument(),
        meetingsToUpdate = [];
      _.each(meetings, function (selM) {
        if (!_.findWhere(existingMeetings, {
          id: selM.getId()
        })) {
          selM.addDocument(doc);
          meetingsToUpdate.push(selM);
        }
      });
      _.each(existingMeetings, function (extM) {
        if (!_.findWhere(meetings, {
          id: extM.getId()
        })) {
          extM.removeDocument(doc);
          meetingsToUpdate.push(extM);
        }
      });
      return Promise.all(_.invoke(meetingsToUpdate, 'requestUpdate')).bind(this).tap(function () {
        this.update('meetings');
      });
    },
    requestDocument: function requestDocument() {
      return Document.requestDocument(this.getDocumentId());
    },
    requestTemplate: function requestTemplate() {
      return Promise.resolve(this.getDocument()).bind(this).then(function (doc) {
        var studyId = doc.getStudyId(),
          templateId = doc.getTemplateId(),
          templateRevision = doc.getTemplateRevisionNumber();
        if (doc.getTemplateType() == 'Modification') {
          return Template.requestModificationTemplate(studyId, templateId, templateRevision);
        } else {
          return Template.requestFormTemplate(templateId, templateRevision);
        }
      });
    },
    requestAnswers: function requestAnswers() {
      return Document.requestAnswerData(this.getDocumentId());
    },
    requestPermissions: function requestPermissions() {
      return Document.requestPermissions(this.getDocumentId());
    },
    requestAssignments: function requestAssignments() {
      return Assignments.requestAll({
        documentId: this.getDocumentId()
      });
    },
    requestChecklists: function requestChecklists() {
      return Document.requestOpenChecklistDocuments(this.getDocumentId());
    },
    requestHistoricalChecklists: function requestHistoricalChecklists() {
      return Document.requestHistoricalChecklistDocuments(this.getDocumentId());
    },
    requestDecisionTypes: function requestDecisionTypes() {
      return DocumentDecision.requestTypes(this.getDocumentId());
    },
    requestDecisions: function requestDecisions() {
      return Promise.resolve(this.getDocument()).bind(this).then(function (doc) {
        if (doc.getDecisions()) {
          var decisions = doc.getDecisions();
          doc.setDecisions(null);
          return decisions;
        }
        return DocumentDecision.requestAllByDocumentId(this.getDocumentId());
      });
    },
    requestLetters: function requestLetters() {
      return DocumentLetter.requestAllByDocumentId(this.getDocumentId());
    },
    requestMeetings: function requestMeetings() {
      return Meeting.requestAllByDocumentId(this.getDocumentId());
    },
    hasAnalystAssignment: function hasAnalystAssignment() {
      return this.hasAssignment('IRB_ANALYST');
    },
    hasAssignment: function hasAssignment(assignmentType) {
      return this.getAssignments().hasAssignment(assignmentType);
    },
    currentUserHasAssignment: function currentUserHasAssignment(assignmentType) {
      return this.getAssignments().personHasAssignment(assignmentType, this.getPerson().getId());
    },
    getDocumentActions: function getDocumentActions() {
      var actions = [],
        permissions = this.getPermissions(),
        doc = this.getDocument(),
        workflowService = this.getWorkflowService(),
        openDocButtonText = 'VIEW';
      if (permissions.canWrite) {
        openDocButtonText = 'EDIT/VIEW';
      } else if (permissions.canWriteComments) {
        openDocButtonText = 'REVIEW/COMMENT';
      }
      if (permissions.canRead) {
        actions.push({
          action: 'edit',
          text: openDocButtonText,
          icon: 'edit'
        });
      }
      if (!_.isEmpty(this.getChecklists()) || doc.finalized && !_.isEmpty(this.getHistoricalChecklists())) {
        actions.push({
          action: 'showChecklist',
          text: 'Show Checklist',
          icon: 'check-square'
        });
      }
      if (!doc.finalizedDate && this.hasAnalystAssignment(doc) && this.checkPermission("IRB_CHANGE_ANALYST") && !workflowService.hasTask('IRB_ANALYST_SELECTED')) {
        actions.push({
          action: 'assignAnalyst',
          text: 'Change Analyst',
          select: true,
          icon: 'user'
        });
      }
      if (permissions.canRead) {
        actions.push({
          action: 'createPDF',
          text: 'Create PDF',
          icon: 'file'
        });
      }
      if (doc.lastReadOnlyDate == null && permissions.canWrite) {
        actions.push({
          action: 'delete',
          text: 'DELETE',
          color: 'danger',
          icon: 'trash-o'
        });
      }
      return actions;
    },
    assignAnalyst: function assignAnalyst(personId) {
      var workflowService = this.getWorkflowService();
      return AssignmentBuilder.requestChangeAnalystAssignment(this.getDocumentId(), personId).bind(this).tap(function () {
        return Promise.join(workflowService.update('tasks'), workflowService.update('taskSummaries'), this.update('assignments'));
      });
    },
    deleteDocument: function deleteDocument() {
      return Document.requestDeleteDocument(this.getDocumentId()).bind(this).tap(function () {
        this.trigger('delete');
      });
    },
    promoteDecision: function promoteDecision(id) {
      return DocumentDecision.requestPromote(id).bind(this).tap(function () {
        return this.update('decisions');
      });
    }
  });
  return DocumentService;
});

